import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../App';

const PAYMENT_TYPES = [
  { value: 'COD',          label: 'Cash on Delivery',            icon: '💵' },
  { value: 'LC',           label: 'Letter of Credit',            icon: '🏦' },
  { value: 'SBLC',         label: 'Standby LC',                  icon: '🏛' },
  { value: 'TT',           label: 'Telegraphic Transfer',        icon: '⚡' },
  { value: 'CAD',          label: 'Cash Against Documents',      icon: '📋' },
  { value: 'DA',           label: 'Documents Against Acceptance',icon: '📝' },
  { value: 'DP',           label: 'Documents Against Payment',   icon: '📄' },
  { value: 'OPEN_ACCOUNT', label: 'Open Account',                icon: '🔓' },
];

const STATUS_META = {
  draft:     { label: 'Entwurf',       bg: '#f8fafc', border: '#e2e8f0', text: '#64748b' },
  active:    { label: 'Aktiv',         bg: '#eff6ff', border: '#93c5fd', text: '#1d4ed8' },
  completed: { label: 'Abgeschlossen', bg: '#f0fdf4', border: '#86efac', text: '#15803d' },
  disputed:  { label: 'Strittig',      bg: '#fef2f2', border: '#fca5a5', text: '#dc2626' },
  cancelled: { label: 'Storniert',     bg: '#f1f5f9', border: '#cbd5e1', text: '#64748b' },
};

const BANK_CONF_META = {
  pending:      { label: 'Ausstehend',  bg: '#fefce8', border: '#fde047', text: '#a16207' },
  confirmed:    { label: 'Bestatigt',   bg: '#f0fdf4', border: '#86efac', text: '#15803d' },
  rejected:     { label: 'Abgelehnt',   bg: '#fef2f2', border: '#fca5a5', text: '#dc2626' },
  not_required: { label: 'Nicht notig', bg: '#f8fafc', border: '#e2e8f0', text: '#64748b' },
};

const RISK_META = {
  LOW:      { label: 'Niedrig',  bg: '#f0fdf4', border: '#86efac', text: '#15803d' },
  MEDIUM:   { label: 'Mittel',   bg: '#fefce8', border: '#fde047', text: '#a16207' },
  HIGH:     { label: 'Hoch',     bg: '#fff7ed', border: '#fdba74', text: '#c2410c' },
  CRITICAL: { label: 'Kritisch', bg: '#fef2f2', border: '#fca5a5', text: '#dc2626' },
};

const CURRENCIES = ['USD', 'EUR', 'GBP', 'CHF', 'NGN'];

const COMMON_RISK_FACTORS = [
  'COD ohne SBLC', 'Kaufer ohne Bankline', 'Unbekannter Counterparty',
  'Zahlungsverzug-Historie', 'Wechselkursrisiko', 'Landerrisiko',
  'Sanktionsrisiko', 'Keine Kreditversicherung', 'Hohe Zahlungssumme',
];

const EMPTY_FORM = {
  deal_reference: '', title: '',
  payment_type: 'COD', payment_days: '', currency: 'USD', total_amount: '',
  buyer_name: '', seller_name: '',
  bank_name: '', bank_swift: '', bank_reference: '',
  bank_confirmation_status: 'pending', bank_confirmation_date: '',
  bank_contact_name: '', bank_contact_email: '',
  risk_level: 'MEDIUM', risk_factors: [], mitigation_measures: '', risk_notes: '',
  status: 'draft', notes: '', tags: [],
};

function Badge({ meta, value }) {
  const m = meta[value] || { label: value, bg: 'var(--bg-card)', border: 'var(--border)', text: 'var(--text3)' };
  return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, background: m.bg, border: `1px solid ${m.border}`, color: m.text, fontSize: 11, fontWeight: 600 }}>{m.label}</span>;
}

function ptTypeMeta(v) { return PAYMENT_TYPES.find(t => t.value === v) || { label: v, icon: '💳' }; }

const input = { width: '100%', padding: '8px 10px', border: '1px solid var(--border)', background: 'var(--bg-surface)', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', color: 'var(--text1)' };
const label = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 4 };
const fieldGroup = { marginBottom: 12 };
const row2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 };
const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: 16, marginBottom: 12 };

export default function PaymentTermsPage() {
  const [records, setRecords]   = useState([]);
  const [summary, setSummary]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [tab, setTab]           = useState('list'); // list | form
  const [activeSection, setActiveSection] = useState('terms');
  const [form, setForm]         = useState(EMPTY_FORM);
  const [editId, setEditId]     = useState(null);
  const [saving, setSaving]     = useState(false);
  const [filterType, setFilterType]     = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterRisk, setFilterRisk]     = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [rList, rSum] = await Promise.all([
        API.get('/payment-terms/'),
        API.get('/payment-terms/summary'),
      ]);
      setRecords(rList.data);
      setSummary(rSum.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function openNew() {
    setForm(EMPTY_FORM); setEditId(null); setActiveSection('terms'); setTab('form');
  }

  function openEdit(rec) {
    setForm({
      deal_reference: rec.deal_reference || '',
      title: rec.title || '',
      payment_type: rec.payment_type || 'COD',
      payment_days: rec.payment_days || '',
      currency: rec.currency || 'USD',
      total_amount: rec.total_amount || '',
      buyer_name: rec.buyer_name || '',
      seller_name: rec.seller_name || '',
      bank_name: rec.bank_name || '',
      bank_swift: rec.bank_swift || '',
      bank_reference: rec.bank_reference || '',
      bank_confirmation_status: rec.bank_confirmation_status || 'pending',
      bank_confirmation_date: rec.bank_confirmation_date || '',
      bank_contact_name: rec.bank_contact_name || '',
      bank_contact_email: rec.bank_contact_email || '',
      risk_level: rec.risk_level || 'MEDIUM',
      risk_factors: rec.risk_factors || [],
      mitigation_measures: rec.mitigation_measures || '',
      risk_notes: rec.risk_notes || '',
      status: rec.status || 'draft',
      notes: rec.notes || '',
      tags: rec.tags || [],
    });
    setEditId(rec.id); setActiveSection('terms'); setTab('form');
  }

  async function save() {
    if (!form.title.trim()) { setError('Titel ist erforderlich'); return; }
    setSaving(true); setError('');
    try {
      if (editId) {
        await API.patch(`/payment-terms/${editId}`, form);
      } else {
        await API.post('/payment-terms/', form);
      }
      await load(); setTab('list');
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  }

  async function deleteRecord(id) {
    if (!window.confirm('Payment Terms loschen?')) return;
    try {
      await API.delete(`/payment-terms/${id}`);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  }

  function toggleRiskFactor(f) {
    setForm(prev => ({
      ...prev,
      risk_factors: prev.risk_factors.includes(f)
        ? prev.risk_factors.filter(x => x !== f)
        : [...prev.risk_factors, f],
    }));
  }

  const filtered = records.filter(r => {
    if (filterType   && r.payment_type !== filterType)   return false;
    if (filterStatus && r.status !== filterStatus)       return false;
    if (filterRisk   && r.risk_level !== filterRisk)     return false;
    return true;
  });

  const sectionTab = (active) => ({
    padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500,
    background: active ? '#22c55e' : 'var(--bg-card)',
    color: active ? '#000' : 'var(--text2)',
    border: active ? 'none' : '1px solid var(--border)',
  });

  if (tab === 'form') {
    return (
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '20px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <button style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text2)', cursor: 'pointer', fontSize: 13 }} onClick={() => setTab('list')}>Zuruck</button>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text1)' }}>
            {editId ? 'Payment Terms bearbeiten' : 'Neue Payment Terms'}
          </h2>
        </div>

        {error && <div style={{ color: '#ef4444', background: '#ef444415', padding: 10, borderRadius: 6, marginBottom: 12 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          {[
            { key: 'terms', label: '💳 Zahlungsbedingungen' },
            { key: 'bank',  label: '🏦 Bank-Bestatigung' },
            { key: 'risk',  label: '⚠️ Risikobewertung' },
          ].map(s => (
            <button key={s.key} style={sectionTab(activeSection === s.key)} onClick={() => setActiveSection(s.key)}>{s.label}</button>
          ))}
        </div>

        {activeSection === 'terms' && (
          <div style={card}>
            <h3 style={{ margin: '0 0 12px', fontSize: 15, color: 'var(--text1)' }}>Zahlungsbedingungen</h3>
            <div style={fieldGroup}><label style={label}>Titel *</label><input style={input} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="z.B. MOH NLCO Crude — Deal 2026-04" /></div>
            <div style={row2}>
              <div style={fieldGroup}><label style={label}>Deal-Referenz</label><input style={input} value={form.deal_reference} onChange={e => setForm(f => ({ ...f, deal_reference: e.target.value }))} placeholder="z.B. DEAL-2026-001" /></div>
              <div style={fieldGroup}><label style={label}>Status</label><select style={input} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>{Object.entries(STATUS_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}</select></div>
            </div>
            <div style={row2}>
              <div style={fieldGroup}><label style={label}>Zahlungsart</label><select style={input} value={form.payment_type} onChange={e => setForm(f => ({ ...f, payment_type: e.target.value }))}>{PAYMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}</select></div>
              <div style={fieldGroup}><label style={label}>Zahlungsfrist</label><input style={input} value={form.payment_days} onChange={e => setForm(f => ({ ...f, payment_days: e.target.value }))} placeholder="z.B. COD+10 Tage" /></div>
            </div>
            <div style={row2}>
              <div style={fieldGroup}><label style={label}>Gesamtbetrag</label><input style={input} value={form.total_amount} onChange={e => setForm(f => ({ ...f, total_amount: e.target.value }))} placeholder="z.B. 12500000" /></div>
              <div style={fieldGroup}><label style={label}>Wahrung</label><select style={input} value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}>{CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
            </div>
            <div style={row2}>
              <div style={fieldGroup}><label style={label}>Kaufer (Buyer)</label><input style={input} value={form.buyer_name} onChange={e => setForm(f => ({ ...f, buyer_name: e.target.value }))} /></div>
              <div style={fieldGroup}><label style={label}>Verkaufer (Seller)</label><input style={input} value={form.seller_name} onChange={e => setForm(f => ({ ...f, seller_name: e.target.value }))} /></div>
            </div>
            <div style={fieldGroup}><label style={label}>Notizen</label><textarea style={{ ...input, minHeight: 60 }} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
          </div>
        )}

        {activeSection === 'bank' && (
          <div style={card}>
            <h3 style={{ margin: '0 0 12px', fontSize: 15, color: 'var(--text1)' }}>Bank-Bestatigung</h3>
            <div style={row2}>
              <div style={fieldGroup}><label style={label}>Bank Name</label><input style={input} value={form.bank_name} onChange={e => setForm(f => ({ ...f, bank_name: e.target.value }))} /></div>
              <div style={fieldGroup}><label style={label}>SWIFT / BIC</label><input style={input} value={form.bank_swift} onChange={e => setForm(f => ({ ...f, bank_swift: e.target.value }))} /></div>
            </div>
            <div style={row2}>
              <div style={fieldGroup}><label style={label}>Bank-Referenz</label><input style={input} value={form.bank_reference} onChange={e => setForm(f => ({ ...f, bank_reference: e.target.value }))} /></div>
              <div style={fieldGroup}><label style={label}>Bestatigunsstatus</label><select style={input} value={form.bank_confirmation_status} onChange={e => setForm(f => ({ ...f, bank_confirmation_status: e.target.value }))}>{Object.entries(BANK_CONF_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}</select></div>
            </div>
            <div style={row2}>
              <div style={fieldGroup}><label style={label}>Bestatigunsdatum</label><input type="date" style={input} value={form.bank_confirmation_date} onChange={e => setForm(f => ({ ...f, bank_confirmation_date: e.target.value }))} /></div>
              <div style={fieldGroup}><label style={label}>Bankkontakt (Name)</label><input style={input} value={form.bank_contact_name} onChange={e => setForm(f => ({ ...f, bank_contact_name: e.target.value }))} /></div>
            </div>
            <div style={fieldGroup}><label style={label}>Bankkontakt (Email)</label><input style={input} value={form.bank_contact_email} onChange={e => setForm(f => ({ ...f, bank_contact_email: e.target.value }))} /></div>
          </div>
        )}

        {activeSection === 'risk' && (
          <div style={card}>
            <h3 style={{ margin: '0 0 12px', fontSize: 15, color: 'var(--text1)' }}>Risikobewertung</h3>
            <div style={fieldGroup}>
              <label style={label}>Risikostufe</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {Object.entries(RISK_META).map(([v, m]) => (
                  <button key={v} type="button"
                    onClick={() => setForm(f => ({ ...f, risk_level: v }))}
                    style={{
                      padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13,
                      background: form.risk_level === v ? m.bg : 'var(--bg-card)',
                      border: `2px solid ${form.risk_level === v ? m.border : 'var(--border)'}`,
                      color: form.risk_level === v ? m.text : 'var(--text3)',
                    }}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={fieldGroup}>
              <label style={label}>Risikofaktoren</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {COMMON_RISK_FACTORS.map(f => (
                  <button key={f} type="button"
                    onClick={() => toggleRiskFactor(f)}
                    style={{
                      padding: '4px 10px', borderRadius: 12, cursor: 'pointer', fontSize: 12,
                      background: form.risk_factors.includes(f) ? '#22c55e20' : 'var(--bg-card)',
                      border: `1px solid ${form.risk_factors.includes(f) ? '#22c55e40' : 'var(--border)'}`,
                      color: form.risk_factors.includes(f) ? '#22c55e' : 'var(--text3)',
                    }}>
                    {f}
                  </button>
                ))}
              </div>
            </div>
            <div style={fieldGroup}><label style={label}>Mitigation</label><textarea style={{ ...input, minHeight: 60 }} value={form.mitigation_measures} onChange={e => setForm(f => ({ ...f, mitigation_measures: e.target.value }))} placeholder="Risikominderungsmasnahmen..." /></div>
            <div style={fieldGroup}><label style={label}>Risiko-Notizen</label><textarea style={{ ...input, minHeight: 60 }} value={form.risk_notes} onChange={e => setForm(f => ({ ...f, risk_notes: e.target.value }))} /></div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={() => setTab('list')} style={{ padding: '9px 20px', borderRadius: 7, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text2)', cursor: 'pointer', fontSize: 13 }}>Abbrechen</button>
          <button onClick={save} disabled={saving} style={{ padding: '9px 20px', borderRadius: 7, border: 'none', background: saving ? '#16a34a' : '#22c55e', color: '#000', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            {saving ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '20px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text1)', margin: 0 }}>💳 Payment Terms</h1>
          <p style={{ fontSize: 13, color: 'var(--text3)', margin: '4px 0 0' }}>Zahlungsbedingungen, Bank-Bestatigung, Risikobewertung</p>
        </div>
        <button style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: '#22c55e', color: '#000', fontSize: 13, fontWeight: 600, cursor: 'pointer' }} onClick={openNew}>+ Neue Payment Terms</button>
      </div>

      {summary && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
          {[
            { label: 'Gesamt', value: summary.total, color: '#22c55e' },
            { label: 'Aktiv', value: summary.by_status?.active || 0, color: '#3b82f6' },
            { label: 'Strittig', value: summary.by_status?.disputed || 0, color: '#ef4444' },
          ].map(s => (
            <div key={s.label} style={{ ...card, padding: '10px 16px', marginBottom: 0, minWidth: 80, textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ ...input, width: 'auto', padding: '6px 10px' }}>
          <option value="">Alle Typen</option>
          {PAYMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...input, width: 'auto', padding: '6px 10px' }}>
          <option value="">Alle Status</option>
          {Object.entries(STATUS_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
        </select>
        <select value={filterRisk} onChange={e => setFilterRisk(e.target.value)} style={{ ...input, width: 'auto', padding: '6px 10px' }}>
          <option value="">Alle Risiken</option>
          {Object.entries(RISK_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
        </select>
      </div>

      {loading && <div style={{ color: 'var(--text3)', fontSize: 13 }}>Lade...</div>}
      {error && <div style={{ color: '#ef4444', background: '#ef444415', padding: 10, borderRadius: 6, marginBottom: 12 }}>{error}</div>}

      {!loading && filtered.length === 0 && (
        <div style={{ ...card, textAlign: 'center', color: 'var(--text3)', padding: 32 }}>
          Keine Payment Terms gefunden.
        </div>
      )}

      {filtered.map(rec => {
        const typeMeta = ptTypeMeta(rec.payment_type);
        return (
          <div key={rec.id} style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 20 }}>{typeMeta.icon}</span>
                  <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text1)' }}>{rec.title}</span>
                  <Badge meta={STATUS_META} value={rec.status} />
                  <Badge meta={RISK_META} value={rec.risk_level} />
                  <Badge meta={BANK_CONF_META} value={rec.bank_confirmation_status} />
                </div>
                <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>
                  {typeMeta.label}
                  {rec.payment_days && <span> · {rec.payment_days}</span>}
                  {rec.deal_reference && <span> · Deal: <strong>{rec.deal_reference}</strong></span>}
                  {rec.total_amount && <span> · {rec.total_amount} {rec.currency}</span>}
                </div>
                {(rec.buyer_name || rec.seller_name) && (
                  <div style={{ fontSize: 12, color: 'var(--text1)' }}>
                    {rec.buyer_name && <span>Kaufer: {rec.buyer_name}</span>}
                    {rec.buyer_name && rec.seller_name && <span> · </span>}
                    {rec.seller_name && <span>Verkaufer: {rec.seller_name}</span>}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button onClick={() => openEdit(rec)} style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text2)', fontSize: 12, cursor: 'pointer' }}>Bearbeiten</button>
                <button onClick={() => deleteRecord(rec.id)} style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #ef444440', background: '#ef444415', color: '#ef4444', fontSize: 12, cursor: 'pointer' }}>🗑</button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
